import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.collateralApiEnv;
const CONVERT_XML_API = appConfig.apis.convertXmlApiEnv;
const PERSON_API = appConfig.apis.personApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string, public data?: any) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) return res.json();
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data?.message || `API error: ${res.status}`, data);
};

const get = async (path: string) => handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'PUT', headers: await getHeaders(), body: JSON.stringify(data) }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const del = async (path: string, data?: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'DELETE', headers: await getHeaders(), body: data ? JSON.stringify(data) : undefined }));

const qs = (params: Record<string, unknown>) => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') s.set(k, String(v)); });
    const str = s.toString();
    return str ? `?${str}` : '';
};

export const collateralApi = {
    // === Verification Configs (was /guarantees + /collateral-analysis) ===

    getVerificationConfigs: (params?: {
        page?: number; pageSize?: number;
        search?: string; priority?: string;
        guaranteeStatus?: string; intensity?: string;
        operationType?: string; obligationType?: string;
        validationStatus?: string; finalStatus?: string;
        sortField?: string; sortOrder?: string;
        showInactive?: string;
    }) => get(`/verification-configs/page-list${qs(params || {})}`),

    getVerificationConfigView: (uuid: string) => get(`/verification-configs/${uuid}/page-view`),

    getVerificationConfigSection: (creditOperationId: string) => get(`/verification-configs/${creditOperationId}/page-section`),

    getConfigDrawees: (uuid: string) => get(`/verification-configs/${uuid}/drawees`),

    createVerificationConfig: (data: {
        credit_operation_id: string;
        criteria: { criteria_type: string; timing: string }[];
        receivable_ids: string[];
        receivables?: { receivable_id: string; debtor_cpf_cnpj: string; debtor_name?: string; [key: string]: unknown }[];
        analysis_mode?: string;
        sample_count?: number;
        operation_code?: string;
        operation_type?: string;
        obligation_type?: string;
        assignor_name?: string;
        assignor_tax_id?: string;
        total_receivables?: number;
        total_amount?: number;
        trader_name?: string;
    }) => post(`/verification-configs`, data),

    updateVerificationConfig: (uuid: string, data: Record<string, unknown>) =>
        put(`/verification-configs/${uuid}`, data),

    editVerificationCriteria: (uuid: string, criteria: { criteria_type: string; timing: string }[], extra?: { operationType?: string; obligationType?: string; traderName?: string; analysisMode?: string; sampledTitles?: number; debtorNotificationDone?: boolean }) =>
        put(`/verification-configs/${uuid}/criteria`, { criteria, ...extra }),

    syncReceivables: (uuid: string, receivables: { receivable_id: string; nfe_key?: string; receivable_number?: string; title_number?: string; debtor_cpf_cnpj: string; debtor_name?: string; city?: string; state?: string; emission_date?: string; due_date?: string; amount?: number }[]) =>
        post(`/verification-configs/${uuid}/sync-receivables`, { receivables }),

    deactivateVerificationConfig: (uuid: string, reason?: string) => del(`/verification-configs/${uuid}`, { reason: reason || '' }),

    restoreVerificationConfig: (uuid: string, reason?: string) => post(`/verification-configs/${uuid}/restore`, { reason: reason || '' }),

    registerChecks: (uuid: string, receivableIds: string[], checks: { criteria_type: string; received?: boolean; status?: string; observation?: string }[], receivableStatus?: string, receivableObservation?: string) =>
        put(`/verification-configs/${uuid}/checks`, { receivable_ids: receivableIds, checks, receivable_status: receivableStatus, receivable_observation: receivableObservation }),

    // === Receivables ===

    getReceivables: (params?: {
        page?: number; pageSize?: number;
        search?: string; status?: string;
        allocationStatus?: string;
        sortField?: string; sortOrder?: string;
    }) => get(`/receivables/page-list${qs(params || {})}`),

    updateReceivableCity: (receivableId: string, cityId: number, creditOperationId?: string) =>
        put(`/receivables/${receivableId}/city`, { cityId, creditOperationId }),

    getReceivable: (receivableId: string) => get(`/receivables/${receivableId}`),

    // === Credit Rights (Pismo registration) ===

    getCreditRights: (params?: {
        page?: number; pageSize?: number;
        search?: string; status?: string;
        creditOperationId?: string;
        sortField?: string; sortOrder?: string;
    }) => get(`/credit-rights/page-list${qs(params || {})}`),

    reprocessCreditRights: (creditOperationId: string) =>
        post(`/credit-rights/reprocess`, { creditOperationId }),

    resendCreditRights: (ids: number[]) =>
        post(`/credit-rights/resend`, { ids }),

    // === Obligations ===

    getObligations: (params?: {
        page?: number; pageSize?: number;
        search?: string; operationType?: string;
        sortField?: string; sortOrder?: string;
    }) => get(`/obligations/page-list${qs(params || {})}`),

    // === Vehicles ===

    getVehicles: (params?: {
        page?: number; pageSize?: number;
        search?: string; vehicleType?: string;
        archived?: string;
        sortField?: string; sortOrder?: string;
    }) => get(`/vehicles/page-list${qs(params || {})}`),

    getVehicleView: (uuid: string) => get(`/vehicles/${uuid}/page-view`),

    createVehicle: (data: {
        name: string;
        vehicle_type: string;
        brand: string;
        model: string;
        chassis: string;
        renavam: string;
        plate: string;
        year_manufacture: number;
        year_model: number;
        plate_state: string;
        color?: string;
        comments?: string;
        credit_operation_id: string;
        owner: { person_id: string; name: string; document: string };
        valuation?: { market_value: number; eligible_value: number; monthly_rate: number; source_name: string; valuation_date: string };
        costs?: { cost_type: string; value: number; cost_date: string; registration_number?: string; registration_date?: string; description?: string }[];
    }) => post(`/vehicles`, data),

    updateVehicle: (uuid: string, data: Record<string, unknown>) =>
        put(`/vehicles/${uuid}`, data),

    addVehicleCost: (uuid: string, data: { cost_type: string; value: number; cost_date: string; registration_number?: string; registration_date?: string; description?: string }) =>
        post(`/vehicles/${uuid}/costs`, data),

    // === Drawee Confirmations (was /validation) ===

    getDraweeConfirmations: (params?: {
        page?: number; pageSize?: number;
        status?: string; search?: string;
        sortField?: string; sortOrder?: string;
        priority?: string;
    }) => get(`/drawee-confirmations/page-list${qs(params || {})}`),

    getDraweeConfirmationTotals: () => get(`/drawee-confirmations/totals`),

    getDraweeConfirmationView: (uuid: string) => get(`/drawee-confirmations/${uuid}/page-view`),

    confirmDrawee: (uuid: string, data: { receivable_ids?: string[]; channel: string; user?: string }) =>
        put(`/drawee-confirmations/${uuid}`, data),

    updateDraweeConfirmation: (uuid: string, data: Record<string, unknown>) =>
        put(`/drawee-confirmations/${uuid}`, data),

    revokeDraweeConfirmation: (uuid: string, data?: { user?: string }) =>
        del(`/drawee-confirmations/${uuid}`, data),

    recordInteraction: (uuid: string, action: string, observation?: string) =>
        post(`/drawee-confirmations/${uuid}/interactions`, { action, observation }),

    assignContact: (uuid: string, contactId: string) =>
        put(`/drawee-confirmations/${uuid}/contact`, { contact_id: contactId }),

    rejectReceivable: (uuid: string, receivableId: string, observation: string) =>
        put(`/drawee-confirmations/${uuid}/receivables/${receivableId}/rejection`, { observation }),

    revokeRejection: (uuid: string, receivableId: string) =>
        del(`/drawee-confirmations/${uuid}/receivables/${receivableId}/rejection`),

    // === Convert XML (unchanged) ===

    getOperationData: async (operationCode: string, operationId?: string) => {
        const headers = await getHeaders();
        headers['x-api-key'] = CONVERT_XML_API.key;
        let id = operationId;
        if (!id) {
            const listRes = await fetch(`${CONVERT_XML_API.address}/operations?search=${encodeURIComponent(operationCode)}&pageSize=10`, { headers });
            const listData = await listRes.json();
            const op = listData?.list?.find((o: Record<string, unknown>) => o.operationCode === operationCode);
            if (!op) throw new ApiError(404, 'Operation not found');
            id = op.id;
        }
        const res = await fetch(`${CONVERT_XML_API.address}/operations/${id}`, { headers });
        return handleResponse(res);
    },

    // === Person search (unchanged) ===

    searchPerson: async (query: string): Promise<{ id: string; name: string; socialId: string }[]> => {
        const headers = await getHeaders();
        headers['x-api-key'] = PERSON_API.key;
        const res = await fetch(`${PERSON_API.address}/individual/page-list?name=${encodeURIComponent(query)}&page=0&size=10&sort=id,DESC`, { headers });
        if (!res.ok) return [];
        const data = await res.json();
        return (data.list || []).map((p: any) => ({ id: p.id || p.personId, name: p.name || p.fullName, socialId: p.socialId || '' }));
    },

    searchLegalPerson: async (query: string): Promise<any[]> => {
        const headers = await getHeaders();
        headers['x-api-key'] = PERSON_API.key;
        const res = await fetch(`${PERSON_API.address}/search/legal_person/${encodeURIComponent(query)}`, { headers });
        if (!res.ok) return [];
        const data = await res.json();
        return data.results || [];
    },

    getInternalManagers: async (): Promise<string[]> => {
        const headers = await getHeaders();
        headers['x-api-key'] = PERSON_API.key;
        const res = await fetch(`${PERSON_API.address}/legal-persons/internal-managers`, { headers });
        if (!res.ok) return [];
        const data = await res.json();
        return data.list || [];
    },
};
